import { Link } from 'react-router-dom';
import styles from './Philosophy.module.css';

const PRINCIPLES = [
  {
    title: 'Why Simulations Matter',
    content: `
      Physical intuition is built through observation and manipulation. Traditional laboratories provide
      this, but are limited by equipment, time, and safety constraints. Virtual laboratories remove
      these constraints while preserving the essential cycle: predict, observe, compare, refine.

      Simulations allow students to encounter phenomena that are otherwise inaccessible—atomic-scale
      interactions, astronomical time scales, extreme conditions. More importantly, they allow systematic
      exploration. Students can vary parameters methodically, observe trends, and develop quantitative
      understanding that qualitative demonstrations cannot provide.

      The value of simulation lies not in replacing physical laboratories, but in complementing them.
      Students who understand the theory through simulation can use physical lab time more effectively.
      They know what to look for, what variables matter, and what constitutes meaningful data.
    `,
  },
  {
    title: 'Why Correctness Over Convenience',
    content: `
      Educational software often simplifies physics for easier visualization or smoother user experience.
      Pendulums that never lose energy. Circuits that reach steady state instantly. Gases that always
      behave ideally. These simplifications create misconceptions that must be unlearned later.

      AxLabs chooses correctness. Real pendulums damp. Real circuits have transient responses. Real gases
      deviate from ideal behavior. Students encounter these complexities from the beginning. They learn
      that models have limits, that assumptions matter, that real data is messy.

      This approach is less convenient in the short term. Students may struggle with simulations that
      don't behave intuitively. But the struggle is productive. It forces engagement with the actual
      theory, not the simplified version. Understanding built on correct foundations supports future
      learning; understanding built on simplifications eventually collapses.
    `,
  },
  {
    title: 'Why Failure Modes Are Features',
    content: `
      Many educational platforms are designed to prevent failure. They guide students toward correct
      answers, provide hints when they struggle, and ensure everyone succeeds. This feels supportive
      but undermines learning.

      Failure is information. An incorrect prediction reveals a misconception. A simulation that diverges
      indicates numerical instability. An experiment that produces no data suggests poor experimental
      design. These failures are more educational than seamless success.

      AxLabs allows experiments to fail. Students can choose parameters that break the simulation.
      Measurements can be invalid if conditions aren't met. Results can be nonsensical if theory is
      misapplied. This is not a bug—it is the essence of scientific inquiry. Learning to diagnose
      failure is more valuable than learning to avoid it.

      The role of instruction is not to prevent failure, but to create a space where failure is safe
      and productive. Students learn that scientific understanding grows from failed predictions,
      revised models, and refined experiments.
    `,
  },
  {
    title: 'Why No Gamification',
    content: `
      Gamification works—points, badges, leaderboards drive engagement. But engagement with what?
      Students focused on achievements optimize for the reward system, not for understanding. They
      learn to exploit the game mechanics rather than the subject matter.

      AxLabs has no points because learning is not a score. No badges because understanding is not
      a collection. No leaderboards because science is not a competition. The only reward is
      comprehension and the only progress is knowledge.

      This makes AxLabs less addictive than educational games. Students will not obsessively complete
      experiments for digital rewards. But the engagement they do experience is with the material,
      not the platform. They return because they want to understand, not because they want to level up.

      Intrinsic motivation—curiosity, the desire to understand, the satisfaction of solved problems—is
      fragile. Extrinsic rewards crowd it out. AxLabs protects intrinsic motivation by refusing to
      manipulate it.
    `,
  },
  {
    title: 'Why Open Infrastructure',
    content: `
      Educational technology is often proprietary, closed, and controlled. Institutions become dependent
      on vendors who can change terms, raise prices, or discontinue products. Teachers have no insight
      into how algorithms work. Researchers cannot validate methods.

      AxLabs is infrastructure, not a platform. Everything is documented: the equations, the numerical
      methods, the validation procedures. Institutions can host their own instances. Developers can
      extend the system. Researchers can audit the code.

      This openness enables long-term trust. Educators can verify that simulations are correct.
      Institutions know the platform won't disappear if a company pivots. The community can maintain
      the system indefinitely.

      Open infrastructure also serves pedagogical goals. Students can see how simulations are built.
      Advanced students can modify experiments or create new ones. The platform itself becomes a learning
      tool, demonstrating how computational science is practiced.
    `,
  },
];

export function Philosophy() {
  return (
    <div className={styles.philosophy}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerContainer}>
          <Link to="/" className={styles.backLink}>← Back to Home</Link>
          <h1 className={styles.title}>Philosophy</h1>
          <p className={styles.subtitle}>
            The principles that guide AxLabs development and distinguish it from
            conventional educational technology.
          </p>
        </div>
      </header>

      {/* Intro */}
      <section className={styles.section}>
        <div className={styles.sectionContainer}>
          <div className={styles.prose}>
            <p className={styles.proseText}>
              Educational technology faces a dilemma: optimize for engagement or optimize for learning.
              Most platforms choose engagement—games, points, simplified content, guided paths.
              Students enjoy these platforms. Do they learn from them?
            </p>
            <p className={styles.proseText}>
              AxLabs optimizes for learning, even when it reduces engagement. We prioritize correct
              physics over smooth animations. We allow experiments to fail. We avoid gamification
              entirely. We build open infrastructure rather than proprietary platforms.
            </p>
            <p className={styles.proseText}>
              These choices are principled, not accidental. Each reflects a belief about how students
              develop genuine understanding. Each is explained below.
            </p>
          </div>
        </div>
      </section>

      {/* Principles */}
      {PRINCIPLES.map((principle, index) => (
        <section key={principle.title} className={index % 2 === 0 ? styles.sectionAlt : styles.section}>
          <div className={styles.sectionContainer}>
            <div className={styles.principleHeader}>
              <span className={styles.principleNumber}>{String(index + 1).padStart(2, '0')}</span>
              <h2 className={styles.principleTitle}>{principle.title}</h2>
            </div>
            <div className={styles.principleContent}>
              {principle.content.split('\n\n').map((paragraph, i) => (
                <p key={i} className={styles.principleText}>{paragraph}</p>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* Summary */}
      <section className={styles.sectionAlt}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>In Summary</h2>
          <div className={styles.summaryBox}>
            <p className={styles.summaryText}>
              AxLabs is built on a simple premise: students learn science by doing science.
              Doing science means making predictions, running experiments, observing results,
              and refining understanding. It means encountering failure and revising models.
              It means engaging with real complexity, not simplified versions.
            </p>
            <p className={styles.summaryText}>
              This approach is less convenient than alternatives. It is less engaging in the
              short term. It produces more confusion and frustration. But the confusion is
              productive, the frustration is temporary, and the understanding that follows
              is genuine and lasting.
            </p>
            <p className={styles.summaryText}>
              Educational institutions that prioritize correctness over convenience,
              understanding over engagement, and long-term value over short-term metrics
              will find AxLabs aligned with their mission.
            </p>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className={styles.section}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Related Reading</h2>
          <div className={styles.relatedList}>
            <Link to="/about" className={styles.relatedLink}>
              <span className={styles.relatedTitle}>About AxLabs</span>
              <span className={styles.relatedDesc}>History, team, and technical philosophy</span>
            </Link>
            <Link to="/research" className={styles.relatedLink}>
              <span className={styles.relatedTitle}>Research & Validation</span>
              <span className={styles.relatedDesc}>Numerical methods and validation framework</span>
            </Link>
            <a href="https://github.com/staticpayload/axlabs" className={styles.relatedLink} target="_blank" rel="noopener noreferrer">
              <span className={styles.relatedTitle}>Source Code</span>
              <span className={styles.relatedDesc}>Contribute and inspect the implementation</span>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className={styles.footer}>
        <div className={styles.footerContainer}>
          <Link to="/curriculum" className={styles.footerLink}>View Curriculum</Link>
          <span className={styles.footerSeparator}>·</span>
          <Link to="/experiments" className={styles.footerLink}>Browse Experiments</Link>
          <span className={styles.footerSeparator}>·</span>
          <Link to="/contact" className={styles.footerLink}>Contact</Link>
        </div>
      </footer>
    </div>
  );
}
